import type { ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useOutletContext, useParams } from "react-router-dom";
import { apiService } from "@/services/apiService";
import { useTranslations } from "@/hooks/useTranslations";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useSupportedLocale } from "@/hooks/useSupportedLocale";
import { isValidLocaleSegment } from "@/utils/localeRouteHelpers";
import type { IWhatsNewResponseDto } from "@/types/whatsNewTypes";
import { MarkdownContent } from "@/components/shared/Markdown/MarkdownContent";
import { Pagination } from "@/components/shared/Pagination/Pagination";
import { ErrorPage } from "@/pages/ErrorPage/ErrorPage";
import { ArrowUpRightRegular } from "@fluentui/react-icons";
import styles from "./WhatsNewPage.module.scss";

interface ISectionItem {
  id: string;
  title: string;
}

interface IOutletContext {
  setSections: (sections: ISectionItem[]) => void;
  setRightNavTitle: (title: string | undefined) => void;
}

export function WhatsNewPage(): ReactNode {
  const params = useParams();
  const location = useLocation();
  const { t } = useTranslations();
  const { setSections, setRightNavTitle } = useOutletContext<IOutletContext>();

  const localeParam = params.locale;
  const { isLocaleValid, isLoading: isLocaleCheckLoading } = useSupportedLocale(localeParam);

  const pageTitle = t("whatsNewPage.title");
  usePageTitle(pageTitle);

  useEffect(() => {
    setRightNavTitle(pageTitle);

    return (): void => {
      setRightNavTitle(undefined);
    };
  }, [pageTitle, setRightNavTitle]);

  const [data, setData] = useState<IWhatsNewResponseDto | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const pageSize = 20;

  const sectionsList = useMemo((): ISectionItem[] => {
    if (!isLocaleValid || isLoading || hasError || !data || data.items.length === 0) {
      return [];
    }

    return data.items.map((item) => ({
      id: `whats-new-${item.id}`,
      title: item.contentName,
    }));
  }, [data, hasError, isLoading, isLocaleValid]);

  useEffect(() => {
    setSections(sectionsList);

    return (): void => {
      setSections([]);
    };
  }, [sectionsList, setSections]);

  const dateFormatter = useMemo(() => {
    const localeForDates = isValidLocaleSegment(localeParam) ? localeParam : "en-US";
    return new Intl.DateTimeFormat(localeForDates, {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  }, [localeParam]);

  useEffect(() => {
    setCurrentPage(1);
  }, [localeParam]);

  useEffect(() => {
    let isCancelled = false;

    async function load(): Promise<void> {
      if (!isLocaleValid) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setHasError(false);
        setErrorMessage(null);

        const response = await apiService.getWhatsNew(currentPage, pageSize, localeParam);
        if (!isCancelled) {
          setData(response);
        }
      } catch (err) {
        if (!isCancelled) {
          setHasError(true);
          setErrorMessage(err instanceof Error ? err.message : null);
          setData(null);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    }

    void load();

    return (): void => {
      isCancelled = true;
    };
  }, [currentPage, isLocaleValid, localeParam]);

  const handlePageChange = (page: number): void => {
    if (!data || page < 1 || page > data.totalPages || page === currentPage) {
      return;
    }

    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Handle hash-based scrolling after page loads (e.g. /en-US/whats-new#whats-new-<id>)
  useEffect(() => {
    if (isLoading || hasError || !data) {
      return;
    }

    const hash = location.hash;
    if (!hash) {
      return;
    }

    const targetId = hash.substring(1);

    const scrollTimeout = setTimeout(() => {
      const element = document.getElementById(targetId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);

    return (): void => {
      clearTimeout(scrollTimeout);
    };
  }, [data, hasError, isLoading, location.hash]);

  if (localeParam && !isLocaleValid && !isLocaleCheckLoading) {
    return <ErrorPage variant="notFound" />;
  }

  if (!isLoading && hasError) {
    return (
      <ErrorPage title={t("apiError.title")} message={errorMessage ?? t("apiError.message")} />
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <header className={styles.header}>
          <h1 className="font-size-h1 font-weight-semibold">{pageTitle}</h1>
          <p className={styles.subtitle}>{t("whatsNewPage.subtitle")}</p>
        </header>

        {isLoading ? (
          <div className={styles.status} role="status" aria-label={t("whatsNewPage.loadingAria")}>
            {t("whatsNewPage.loading")}
          </div>
        ) : null}

        {!isLoading && data && data.items.length === 0 ? (
          <div className={styles.status}>{t("whatsNewPage.empty")}</div>
        ) : null}

        {!isLoading && data && data.items.length > 0 ? (
          <>
            <section className={styles.list} aria-label={t("whatsNewPage.listAria")}>
              {data.items.map((item) => {
                const parsedLastContentUpdate = new Date(item.lastContentUpdate);
                const lastContentUpdate = Number.isNaN(parsedLastContentUpdate.getTime())
                  ? ""
                  : dateFormatter.format(parsedLastContentUpdate);
                const hasLink =
                  Boolean(item.contentUrl) && item.contentType !== "eligibilityFolder";
                const sectionId = `whats-new-${item.id}`;

                return (
                  <article key={item.id} className={styles.item}>
                    <div className={styles.itemHeader}>
                      <div className={styles.itemTitleRow}>
                        <div className={styles.itemTitleGroup}>
                          <h2
                            id={sectionId}
                            className={`font-size-h3 font-weight-semibold ${styles.itemTitle}`}
                          >
                            {hasLink ? (
                              <a
                                className={styles.link}
                                href={item.contentUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={t("whatsNewPage.openUpdatedPageAria", {
                                  pageTitle: item.contentName,
                                })}
                              >
                                {item.contentName}
                              </a>
                            ) : (
                              item.contentName
                            )}
                          </h2>
                          {hasLink ? (
                            <a
                              className={styles.openInNewTabLink}
                              href={item.contentUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label={t("whatsNewPage.openUpdatedPageInNewTabAria", {
                                pageTitle: item.contentName,
                              })}
                              title={t("whatsNewPage.openUpdatedPageInNewTabAria", {
                                pageTitle: item.contentName,
                              })}
                            >
                              <ArrowUpRightRegular className={styles.openInNewTabIcon} />
                            </a>
                          ) : null}
                        </div>
                        <p className={styles.meta}>{lastContentUpdate}</p>
                      </div>
                    </div>

                    <div className={styles.itemBody}>
                      <MarkdownContent
                        content={item.aiSummary || t("whatsNewPage.missingSummary")}
                      />
                    </div>
                  </article>
                );
              })}
            </section>

            <Pagination
              ariaLabel={t("whatsNewPage.paginationAria")}
              currentPage={data.currentPage}
              totalPages={data.totalPages}
              previousPageLabel={t("whatsNewPage.previousPage")}
              nextPageLabel={t("whatsNewPage.nextPage")}
              pageIndicator={t("whatsNewPage.pageIndicator", {
                currentPage: data.currentPage.toString(),
                totalPages: data.totalPages.toString(),
              })}
              onPageChange={handlePageChange}
            />
          </>
        ) : null}
      </div>
    </div>
  );
}
